import { useEffect, useRef } from "react";

import type {
  AssistantMessage,
  BubbleMessage,
} from "@/features/assistant/types";

import { MessageBubble } from "./MessageBubble";

interface Props {
  messages: AssistantMessage[];
  pending: BubbleMessage | null;
  pendingUser: string | null;
  onFeedback: (messageId: number, feedback: "up" | "down") => void;
  onRetry: () => void;
  emptyState?: React.ReactNode;
}

export function MessageList({
  messages,
  pending,
  pendingUser,
  onFeedback,
  onRetry,
  emptyState,
}: Props) {
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [messages.length, pending?.content, pending?.blocks.length, pendingUser]);

  const isEmpty = messages.length === 0 && !pending && !pendingUser;

  return (
    <div
      className="flex-1 space-y-3 overflow-y-auto p-3"
      data-testid="assistant-message-list"
    >
      {isEmpty && emptyState}
      {messages.map((m) => (
        <MessageBubble key={m.id} message={{ ...m }} onFeedback={onFeedback} />
      ))}
      {pendingUser && (
        <MessageBubble
          message={{ role: "user", content: pendingUser, blocks: [] }}
        />
      )}
      {pending && (
        <MessageBubble
          message={pending}
          streaming={!pending.error && !pending.id}
          onRetry={onRetry}
        />
      )}
      <div ref={bottom} />
    </div>
  );
}
